/** Small formatting helpers shared by the shell features. */

/** "2026-08-15T21:00:00Z" → "Aug 15, 21:00" (locale-light, stable). */
export function formatTimestamp(iso: string | null): string {
  if (iso === null) return 'n/a';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 0.61 → "$0.61"; 1.9 → "$1.90". Cost numbers render verbatim otherwise. */
export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** 232560 → "232.6k" */
export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1)}k`;
}

export function plural(n: number, singular: string, pluralForm?: string): string {
  return `${n} ${n === 1 ? singular : (pluralForm ?? `${singular}s`)}`;
}

/** "2026-08-20T10:00:03Z" → "2h ago" (falls back to the absolute form). */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const seconds = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatTimestamp(iso);
}

/** 1204 → "1,204" (thousands separators for count displays). */
export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** 3100 → "3.1s"; 800 → "0.8s". */
export function formatDurationMs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * How long a piece of work took, for a reader watching it happen (round 3:
 * "showing some sort of running duration would also be nice"). Seconds with
 * one decimal up to a minute, then minutes and whole seconds: "4.2s",
 * "1m 07s". Negative/NaN clocks read as "0.0s" rather than nonsense.
 */
export function formatElapsedMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0.0s';
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return seconds === 60
    ? `${minutes + 1}m 00s`
    : `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

/** Bytes → readable ("4.2 MB") for the zip upload progress. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

